import React from 'react';
import { Box, Typography } from '@mui/material';
import { brand, neutral } from '../../theme';
import { pluralize } from '../../utils/format';
import { formatMonthKey } from './insightsModel';
import { visuallyHidden } from './visuallyHidden';

interface ProjectsByMonthChartProps {
  months: Array<{ month: string; count: number }>;
}

/** Height of the tallest bar, in px. */
const PLOT_HEIGHT = 160;

/**
 * Projects created per month: solid neutral bars with the value printed on
 * top of each, and the month and year under it. Months with no projects keep
 * a hairline stub so the row of months stays readable.
 */
const ProjectsByMonthChart: React.FC<ProjectsByMonthChartProps> = ({ months }) => {
  const max = Math.max(1, ...months.map((m) => Number(m.count) || 0));
  return (
    <Box
      component="ol"
      aria-label="Projects created per month"
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        display: 'grid',
        gridTemplateColumns: `repeat(${Math.max(1, months.length)}, minmax(0, 1fr))`,
        columnGap: { xs: 1, sm: 2 },
        alignItems: 'end',
      }}
    >
      {months.map((entry) => {
        const count = Math.max(0, Number(entry.count) || 0);
        const label = formatMonthKey(entry.month);
        const spoken = label ? `${label.month} ${label.year}` : entry.month;
        const barHeight = count > 0 ? Math.max(4, Math.round((count / max) * PLOT_HEIGHT)) : 2;
        return (
          <Box
            component="li"
            key={entry.month}
            data-testid="month-bar"
            sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}
          >
            <Box component="span" sx={visuallyHidden}>
              {`${spoken}: ${pluralize(count, 'project')}`}
            </Box>
            <Box
              aria-hidden="true"
              sx={{
                height: PLOT_HEIGHT + 24,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                alignItems: 'center',
                borderBottom: `1px solid ${brand.borderStrong}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: count > 0 ? 'text.primary' : 'text.secondary', mb: 0.5 }}
              >
                {count}
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  maxWidth: 56,
                  height: barHeight,
                  backgroundColor: count > 0 ? brand.slate : neutral[300],
                  borderRadius: '4px 4px 0 0',
                }}
              />
            </Box>
            <Box aria-hidden="true" sx={{ mt: 1, textAlign: 'center', lineHeight: 1.3 }}>
              <Typography variant="caption" component="p" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                {label ? label.month : entry.month}
              </Typography>
              {label && (
                <Typography variant="caption" component="p" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                  {label.year}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default ProjectsByMonthChart;
